// The session lists every signed-in surface shares (SIGNED_IN_PLAN 3.4):
// the sidebar counts, the list column beside an open session, and W2's
// Sessions panes and Liked read and page them here, so a rename, a delete
// or a like shows everywhere at once.
//
//   lists.{mine, friends, live, liked} = { rows, total, nextCursor,
//                                          loading, error, loadedAt }
//   load(place, { reset }), loadMore(place), refresh(place)
//   patch(name, fields), remove(name), upsert(row, place)
//   like(name), unlike(name)       optimistic, POST /add_favorite|remove_favorite
//   counts                         { mine, live, liked } (computed)
//   showsEmpty(list), wantsMore(list)  the panes' empty state and fill (M4)
//
// Requests:
//   Mine     GET /sessions?view=list&limit=50[&cursor]
//   Friends  GET /friends_sessions?view=list&limit=50[&cursor]
//   Live     GET /publicSessionHeaders?view=list&live=1&limit=50[&cursor]
//   Liked    GET /favorite_sessions, reversed so the newest comes first
// Paged answers are {sessions, next_cursor, total}; a bare list (an older
// server) is one page with total = length.
import { computed, reactive } from "vue";
import store from "@/store";
import { apiGet, apiPost } from "@/services/api/client.js";
import { isTestSession } from "@/kit/testAccounts.js";
import { sessionIsLive } from "@/kit/sessionRow.js";

export const PLACES = ["mine", "friends", "live", "liked"];
const PAGE = 50;
// A screen of rows. A page the filters thinned (test accounts, SI-18) is
// not the end of the list: load() keeps asking while fewer than this are
// kept and a cursor is left (M4), a few pages per call.
export const SCREEN_ROWS = 20;
const FILL_PAGES = 4;

const blank = () => ({ rows: [], total: null, nextCursor: null, loading: false, error: null, loadedAt: null });

export const lists = reactive({ mine: blank(), friends: blank(), live: blank(), liked: blank() });

const me = () => store.state.loggedInUser?.username || null;

const REQUESTS = {
  // include_library: your sample library leads the first page (kit/sampleLibrary.js).
  mine: (cursor) => apiGet("/sessions", { view: "list", limit: PAGE, cursor, include_library: 1 }),
  friends: (cursor) => apiGet("/friends_sessions", { view: "list", limit: PAGE, cursor }),
  live: (cursor) => apiGet("/publicSessionHeaders", { view: "list", live: 1, limit: PAGE, cursor }),
  liked: () => apiGet("/favorite_sessions"),
};

function page(res, place) {
  if (Array.isArray(res)) {
    const rows = place === "liked" ? [...res].reverse() : res;
    return { rows, nextCursor: null, total: rows.length };
  }
  if (res && Array.isArray(res.sessions)) {
    return {
      rows: res.sessions,
      nextCursor: res.next_cursor || null,
      total: Number.isFinite(res.total) ? res.total : null,
    };
  }
  return { rows: [], nextCursor: null, total: null };
}

// Friends and Live drop test accounts (SI-18); Live keeps only rows that
// are live to you (SI-22).
function keep(place, rows) {
  let out = rows.filter((r) => r && r.name);
  if (place === "friends" || place === "live") out = out.filter((r) => !isTestSession(r));
  if (place === "live") out = out.filter((r) => sessionIsLive(r, me()));
  return out;
}

const inflight = {};
// Bumped by start() and stop() (signing in, out, or as someone else): an
// answer for the last account lands nowhere (L2).
let epoch = 0;

export async function load(place, { reset = false } = {}) {
  if (!PLACES.includes(place)) return;
  const list = lists[place];
  if (inflight[place] && !reset) return inflight[place];
  const cursor = reset ? null : list.nextCursor;
  if (!reset && list.loadedAt && !cursor) return;
  const myEpoch = epoch;
  const who = me();
  const current = () => myEpoch === epoch && who === me();
  list.loading = true;
  list.error = null;
  let run = null;
  run = (async () => {
    try {
      let { rows, nextCursor, total } = page(await REQUESTS[place](cursor), place);
      if (!current()) return;
      let kept = keep(place, rows);
      const before = reset || !cursor ? 0 : list.rows.length;
      // Live is small: it loads whole. The others fill a screen.
      let guard = 0;
      while (nextCursor && guard++ < FILL_PAGES && (place === "live" || before + kept.length < SCREEN_ROWS)) {
        const more = page(await REQUESTS[place](nextCursor), place);
        if (!current()) return;
        kept = kept.concat(keep(place, more.rows));
        nextCursor = more.nextCursor;
      }
      list.rows = reset || !cursor ? dedupe(kept) : dedupe(list.rows.concat(kept));
      list.nextCursor = nextCursor;
      list.total = place === "live" ? list.rows.length : total;
      list.loadedAt = Date.now();
    } catch (e) {
      if (current()) list.error = e && e.status != null ? e : { status: 0, message: "Couldn't load sessions." };
    } finally {
      if (inflight[place] === run) inflight[place] = null;
      // A stale run leaves a newer one's spinner alone.
      if (current() || !inflight[place]) list.loading = false;
    }
  })();
  inflight[place] = run;
  return run;
}

// The empty state shows only when nothing is left to ask for: no rows, no
// cursor (M4). Rows the filters dropped are not the end of the list.
export function showsEmpty(list) {
  return !!list && !list.rows.length && !list.loading && !list.error && !!list.loadedAt && !list.nextCursor;
}

// A list shorter than a screen with a cursor left: it can't scroll, so
// the panes ask for more themselves.
export function wantsMore(list) {
  return !!list && !!list.nextCursor && !list.loading && !list.error && list.rows.length < SCREEN_ROWS;
}

function dedupe(rows) {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.name) ? false : (seen.add(r.name), true)));
}

export function loadMore(place) {
  const list = lists[place];
  if (!list || list.loading || !list.nextCursor) return Promise.resolve();
  return load(place);
}

export function refresh(place) {
  return load(place, { reset: true });
}

// A session changed (renamed, privacy, picture, a state_update): every
// list's row follows.
export function patch(name, fields) {
  if (!name || !fields) return;
  for (const place of PLACES) {
    const list = lists[place];
    const i = list.rows.findIndex((r) => r.name === name);
    if (i >= 0) list.rows[i] = { ...list.rows[i], ...fields };
  }
}

// A session is gone (deleted, left): out of every list.
export function remove(name) {
  for (const place of PLACES) {
    const list = lists[place];
    const before = list.rows.length;
    list.rows = list.rows.filter((r) => r.name !== name);
    if (list.rows.length !== before && Number.isFinite(list.total)) list.total = Math.max(0, list.total - 1);
  }
}

// A new or changed row goes to the top of `place` (New session, Duplicate).
export function upsert(row, place = "mine") {
  if (!row || !row.name || !PLACES.includes(place)) return;
  const list = lists[place];
  const had = list.rows.some((r) => r.name === row.name);
  list.rows = [row, ...list.rows.filter((r) => r.name !== row.name)];
  if (!had && Number.isFinite(list.total)) list.total += 1;
  patch(row.name, row);
}

function findRow(name) {
  for (const place of PLACES) {
    const r = lists[place].rows.find((x) => x.name === name);
    if (r) return r;
  }
  return null;
}

function setFavorites(names) {
  const user = store.state.loggedInUser;
  if (user) store.commit("setLoggedInUser", { ...user, favorites: names });
}

export async function like(name) {
  const who = me();
  if (!name || !who) return false;
  const favorites = [...(store.state.loggedInUser?.favorites || [])];
  if (favorites.includes(name)) return true;
  const row = findRow(name);
  const before = { favorites, liked: lists.liked.rows };
  setFavorites([...favorites, name]);
  if (row) {
    patch(name, { favorited_by: [...new Set([...(row.favorited_by || []), who])] });
    if (lists.liked.loadedAt) lists.liked.rows = [findRow(name) || row, ...lists.liked.rows.filter((r) => r.name !== name)];
  }
  try {
    await apiPost("/add_favorite", { session_name: name });
    return true;
  } catch (e) {
    setFavorites(before.favorites);
    lists.liked.rows = before.liked;
    if (row) patch(name, { favorited_by: row.favorited_by || [] });
    return false;
  }
}

export async function unlike(name) {
  const who = me();
  if (!name || !who) return false;
  const favorites = [...(store.state.loggedInUser?.favorites || [])];
  const row = findRow(name);
  const before = { favorites, liked: lists.liked.rows };
  setFavorites(favorites.filter((n) => n !== name));
  lists.liked.rows = lists.liked.rows.filter((r) => r.name !== name);
  if (row) patch(name, { favorited_by: (row.favorited_by || []).filter((u) => u !== who) });
  try {
    await apiPost("/remove_favorite", { session_name: name });
    return true;
  } catch (e) {
    setFavorites(before.favorites);
    lists.liked.rows = before.liked;
    if (row) patch(name, { favorited_by: row.favorited_by || [] });
    return false;
  }
}

export function isLiked(name) {
  return (store.state.loggedInUser?.favorites || []).includes(name);
}

// Mine: the server total (your own sessions are never hidden). Live: the
// live rows left. Liked: your favorites. Friends has no count.
export const counts = computed(() => ({
  mine: Number.isFinite(lists.mine.total) ? lists.mine.total : null,
  live: lists.live.loadedAt ? lists.live.rows.length : null,
  liked: (store.state.loggedInUser?.favorites || []).length,
}));

// Live refreshes every 30 s while it is on screen (the Live pane, or the
// list column on Live), every 60 s otherwise; never while the tab is
// hidden.
let liveTimer = null;
let liveOnScreen = 0;
let lastLive = 0;

function tick() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const every = liveOnScreen > 0 ? 30000 : 60000;
  if (Date.now() - lastLive >= every) {
    lastLive = Date.now();
    refresh("live");
  }
}

// A pane showing Live: call on mount, and the returned function on unmount.
export function watchLive() {
  liveOnScreen += 1;
  return () => {
    liveOnScreen = Math.max(0, liveOnScreen - 1);
  };
}

// Signed in: Mine and Live load for the counts. Liked loads when opened;
// Friends on first view.
export function start() {
  stop({ keep: true });
  epoch += 1;
  for (const place of PLACES) inflight[place] = null;
  lastLive = Date.now();
  load("mine", { reset: true });
  load("live", { reset: true });
  liveTimer = setInterval(tick, 10000);
}

export function stop({ keep = false } = {}) {
  if (liveTimer) clearInterval(liveTimer);
  liveTimer = null;
  if (keep) return;
  epoch += 1;
  for (const place of PLACES) {
    inflight[place] = null;
    Object.assign(lists[place], blank());
  }
}

const sessionLists = {
  lists,
  load,
  loadMore,
  refresh,
  patch,
  remove,
  upsert,
  like,
  unlike,
  isLiked,
  counts,
  showsEmpty,
  wantsMore,
  watchLive,
  start,
  stop,
};
export default sessionLists;
